import { Box, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import React from 'react';
import { SrsRtcWhepAsync } from './SrsRtcWhepAsync';

// Template A of the /link page top segment: a self-contained WHEP player.
// Deliberately NOT the shared LiveStream singleton — that one attaches
// tracks to fixed DOM ids that belong to the storefront's floating/full
// players. This instance owns its <video> via ref and tears down cleanly.

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  frame: {
    position: 'relative',
    borderRadius: '14px',
    overflow: 'hidden',
    backgroundColor: '#000000',
    // Portrait-ish frame like a phone livestream.
    aspectRatio: '9 / 13',
    maxHeight: '480px',
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  liveBadge: {
    position: 'absolute',
    top: '10px',
    left: '10px',
    backgroundColor: '#e02020',
    color: '#ffffff',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '1px',
    borderRadius: '6px',
    padding: '3px 8px',
  },
  unmuteOverlay: {
    position: 'absolute',
    bottom: '10px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.62)',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: 600,
    borderRadius: '18px',
    padding: '7px 14px',
    cursor: 'pointer',
    userSelect: 'none',
  },
}),
);

interface LinkPageLivePlayerProps {
  // Full WHEP URL, e.g. `${SRS_HTTP_URL}/rtc/v1/whep/?app=live&stream=${slug}`.
  url: string;
  // Player failed to connect — parent should fall back to the offline segment.
  onPlaybackFailed: () => void;
}

export default function LinkPageLivePlayer({ url, onPlaybackFailed }: LinkPageLivePlayerProps) {
  const classes = useStyles();
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const clientRef = React.useRef<SrsRtcWhepAsync | null>(null);
  // Muted autoplay is the only autoplay mobile browsers allow; the shopper
  // taps to unmute.
  const [muted, setMuted] = React.useState(true);

  React.useEffect(() => {
    let cancelled = false;

    const connect = async () => {
      try {
        const client = new SrsRtcWhepAsync();
        clientRef.current = client;
        client.ontrack = (event: RTCTrackEvent) => {
          const videoEl = videoRef.current;
          if (!videoEl) return;
          if (!videoEl.srcObject) videoEl.srcObject = new MediaStream();
          (videoEl.srcObject as MediaStream).addTrack(event.track);
        };
        await client.play(url);
      } catch (error) {
        console.error('[LINK PAGE] Live player failed:', error);
        if (!cancelled) onPlaybackFailed();
      }
    };

    if (url) connect();

    return () => {
      cancelled = true;
      if (clientRef.current) {
        clientRef.current.close();
        clientRef.current = null;
      }
      const videoEl = videoRef.current;
      if (videoEl && videoEl.srcObject) {
        (videoEl.srcObject as MediaStream).getTracks().forEach((track) => track.stop());
        videoEl.srcObject = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  return (
    <Box className={classes.frame}>
      <video
        ref={videoRef}
        className={classes.video}
        autoPlay
        playsInline
        muted={muted}
      />
      <Typography className={classes.liveBadge}>LIVE</Typography>
      {
        muted &&
        <Typography className={classes.unmuteOverlay} onClick={() => setMuted(false)}>
          Ketuk untuk menyalakan suara 🔊
        </Typography>
      }
    </Box>
  );
}
